import { ChevronDownIcon, ChevronUpIcon, HeartIcon } from '@heroicons/react/20/solid'
import { ChatBubbleLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { FormWrapper } from '@/form-control'
import { TextAreaField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'
import { formatDistanceToNow } from 'date-fns'

import { IComment, ICommentReaction } from '@/MODELS/comment.model'
import { IPost } from '@/MODELS/post.model'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import Avatar from '@/components/Avatar'
import Button from '@/components/Button'

interface IReply {
    comment: IComment
    post: IPost
    depth: number
    get: () => void
    replyComments?: IComment[]
    reactions?: ICommentReaction[]
}

export function Reply({ comment, post, depth, get, replyComments = [] }: IReply) {
    const { user } = useAuth()
    const [showReplyForm, setShowReplyForm] = useState(false)
    const [showReplies, setShowReplies] = useState(depth < 2)
    const [isLiked, setIsLiked] = useState(false)

    const childReplies =
        replyComments?.filter((reply) => reply.parentComment?.toString() === comment?._id?.toString()) || []

    const maxDepth = 4
    const totalReactions = comment.reactions?.reduce((sum, r) => sum + r.count, 0) || 0
    const getIndentStyle = (depth) => {
        if (depth === 0) return ''
        const colors = [
            'border-l-brand-200 bg-gradient-to-r from-blue-50/30 to-transparent',
            'border-l-secondary-100 bg-gradient-to-r from-purple-50/30 to-transparent',
            'border-l-indigo-200 bg-gradient-to-r from-green-50/30 to-transparent',
            'border-l-orange-200 bg-gradient-to-r from-orange-50/30 to-transparent',
        ]
        const colorIndex = (depth - 1) % colors.length
        return `ml-4 pl-4 border-l-2 ${colors[colorIndex]}`
    }

    async function handleReact() {
        try {
            setIsLiked(!isLiked)

            const reaction = {
                commentId: comment?._id,
                reaction: '❤️',
            }

            await api(ROUTES.app.comment + '/reaction', reaction).then(() => {
                get()
            })
        } catch (error) {
            setIsLiked(isLiked)

            console.error('Error updating comment reaction:', error)
        }
    }

    const toggleReplyForm = () => {
        setShowReplyForm(!showReplyForm)
    }

    const toggleReplies = () => {
        setShowReplies(!showReplies)
    }

    return (
        <div className={`${getIndentStyle(depth)} transition-all duration-300 ease-in-out`}>
            <div className='relative p-5 mb-4 transition-all duration-300 border group bg-white/60 backdrop-blur-sm border-gray-100/50 rounded-2xl hover:bg-white/80 hover:border-gray-200/60 hover:shadow-lg hover:shadow-gray-100/50'>
                <div className='relative z-10 flex items-start space-x-4'>
                    <div className='flex-shrink-0'>
                        <div className='relative'>
                            <Avatar user={comment.owner} size={depth > 1 ? 'xs' : 'sm'} />
                        </div>
                    </div>

                    <div className='flex-1 min-w-0'>
                        <div className='flex items-center mb-2 space-x-3'>
                            <span className='text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text'>
                                {comment.owner?.name || 'Anonymous'}
                            </span>
                            <span className='px-2 py-1 text-xs text-gray-500 rounded-full '>
                                {formatDistanceToNow(comment.createdAt)} ago
                            </span>
                        </div>

                        <div className='mb-4'>
                            <p className='text-sm font-medium leading-relaxed text-gray-800 whitespace-pre-wrap'>
                                {comment.body}
                            </p>
                        </div>

                        <div className='flex items-center space-x-2 text-xs'>
                            <button
                                onClick={handleReact}
                                className={`flex items-center space-x-2 transition-colors ${
                                    totalReactions > 0 ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                                }`}
                            >
                                <HeartIcon className={`w-4 h-4 ${isLiked ? 'animate-pulse' : ''}`} />
                                <span>{totalReactions}</span>
                            </button>

                            {depth < maxDepth && (
                                <button
                                    onClick={toggleReplyForm}
                                    className='flex items-center space-x-2 px-3 py-1.5 text-gray-600 bg-gray-50/80 rounded-full hover:bg-brand-50 hover:text-brand-600 transition-all duration-200 font-medium'
                                >
                                    <ChatBubbleLeftIcon className='w-4 h-4' />
                                    <span>Reply</span>
                                </button>
                            )}

                            {childReplies.length > 0 && (
                                <button
                                    onClick={toggleReplies}
                                    className='flex items-center space-x-2 px-3 py-1.5 text-gray-600 bg-gray-50/80 rounded-full hover:bg-purple-50 hover:text-secondary-600 transition-all duration-200 font-medium'
                                >
                                    {showReplies ? (
                                        <>
                                            <ChevronUpIcon className='w-4 h-4' />
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDownIcon className='w-4 h-4' />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        {showReplyForm && (
                            <div className='p-4 mt-6 border shadow-sm bg-gradient-to-br from-gray-50/80 to-white/60 backdrop-blur-sm border-gray-200/50 rounded-xl'>
                                <FormWrapper<IComment>
                                    endpoint={ROUTES.app.comment}
                                    id={null}
                                    insertIntoPostBody={{
                                        owner: user,
                                        post: post,
                                        parentComment: comment,
                                    }}
                                    hideSubmitButton
                                    callbackAfterSubmit={() => {
                                        setShowReplyForm(false)
                                        get()
                                    }}
                                >
                                    {(f, { formValues, submit }) => (
                                        <div className='space-y-4'>
                                            <div className='flex space-x-3'>
                                                <div className='flex-shrink-0'>
                                                    <Avatar user={user} size='sm' />
                                                </div>
                                                <div className='flex-1'>
                                                    <TextAreaField
                                                        label=''
                                                        {...f('body')}
                                                        placeholder={`Reply to ${comment.owner?.name || 'Anonymous'}...`}
                                                        className='w-full min-h-[80px] px-4 py-3 text-sm bg-white/80 border border-gray-200/60 rounded-xl resize-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400/60 focus:bg-white transition-all duration-200 placeholder-gray-400'
                                                    />
                                                </div>
                                            </div>

                                            <div className='flex items-center justify-between w-full gap-x-2'>
                                                <button
                                                    type='button'
                                                    onClick={() => setShowReplyForm(false)}
                                                    className='px-4 py-2 text-sm font-medium text-gray-600 transition-all duration-200 rounded-lg bg-gray-100/80 hover:bg-gray-200/80 hover:text-gray-800'
                                                >
                                                    Cancel
                                                </button>

                                                <Button.Outline
                                                    disabled={!formValues?.body?.trim()}
                                                    className='flex items-center px-5 py-2 space-x-2 text-sm font-medium text-white transition-all duration-200 rounded-lg shadow-sm bg-gradient-to-r from-brand-500 to-brand-600 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md'
                                                    onClickAsync={async () => {
                                                        await submit()
                                                    }}
                                                >
                                                    Reply
                                                    <PaperAirplaneIcon className='w-3.5 h-3.5' />
                                                </Button.Outline>
                                            </div>
                                        </div>
                                    )}
                                </FormWrapper>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showReplies && childReplies.length > 0 && (
                <div className='space-y-3 duration-300 animate-in slide-in-from-top-2'>
                    {childReplies.map((childComment) => (
                        <Reply
                            key={childComment._id}
                            comment={childComment}
                            post={post}
                            depth={depth + 1}
                            get={get}
                            replyComments={replyComments}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
